/**
 * Trends — every tracked number, grouped by the question it answers.
 *
 * WHY THIS IS NO LONGER ONE PANEL. Nine series spanning four orders of magnitude used to
 * share a single log axis, because that was the only way they could share one. It worked
 * and it was unreadable on a phone: nine lines, nine dashed thresholds, one legend of nine
 * toggles, and a y-axis in powers of ten that answers no question anybody asks. Small
 * multiples give every metric its own linear scale — the scale it is actually read on —
 * and keep them comparable by sharing one date domain across every panel.
 *
 * The old "off target" mode is not gone; it moved. Its insight was that the interesting
 * question spans all the metrics at once ("what is drifting"), and that a shortfall and an
 * excess are the same kind of answer. That now lives in "Against your targets" as one meter
 * per metric, which says the same thing in words and bars instead of in a normalised log
 * ratio, and says it on a 375px screen.
 *
 * GROUPING IS DERIVED, not a hardcoded key list: same unit and same question go together
 * (mmHg is the one pair that only means anything read together), food is `source ==
 * 'nutrition'`, and anything else is a body reading. A metric added in D1 tomorrow lands in
 * a group with no code change.
 *
 * TONE. This records; it never advises. A target is a line the user drew, and a value is
 * shown against it as a fact. No praise, no scolding, no streaks.
 */
import { useMemo } from 'react';
import type { WellnessTrendSeries, WellnessTrends } from '../../shared/types';
import { Card, CardTitle, Empty } from '../home/ui';
import {
  DUSK,
  NumbersTable,
  PAIR,
  SeriesChart,
  TargetMeter,
  completeAverage,
  dateDomain,
  fmt,
  targetSide,
} from './ChartKit';
import { shortDay } from './format';

export default function TrendsCharts({ data }: { data: WellnessTrends }) {
  const groups = useMemo(() => {
    const pressure: WellnessTrendSeries[] = [];
    const food: WellnessTrendSeries[] = [];
    const body: WellnessTrendSeries[] = [];
    for (const s of data.series) {
      if (!s.points.length) continue;
      if (s.unit === 'mmHg') pressure.push(s);
      else if (s.source === 'nutrition') food.push(s);
      else body.push(s);
    }
    return { pressure, food, body };
  }, [data.series]);

  const domain = useMemo(() => dateDomain(data.series), [data.series]);
  const tracked = [...groups.pressure, ...groups.food, ...groups.body];
  const withTargets = tracked.filter((s) => s.target !== null);

  if (!tracked.length) {
    return (
      <Card className="p-5">
        <Empty
          message="Nothing to chart yet. Log a reading or a meal and the lines start here — a single day is enough to draw the first point."
          action={{ to: '/wellness', label: 'Log something' }}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {withTargets.length > 0 && <AgainstTargets series={withTargets} />}

      {groups.pressure.length > 0 && (
        <Card className="p-4 sm:p-5">
          <CardTitle icon="pulse">Blood pressure</CardTitle>
          <LatestBig series={groups.pressure} pairAs="/" />
          <div className="mt-3">
            <SeriesChart series={groups.pressure} colors={PAIR} domain={domain} height={186} legend />
          </div>
          <p className="mt-2 text-xs text-ink-faint">
            Dashed lines are the targets set for each. Systolic and diastolic share one axis
            because they share one unit.
          </p>
          <NumbersTable series={groups.pressure} />
        </Card>
      )}

      {groups.food.length > 0 && (
        <Card className="p-4 sm:p-5">
          <CardTitle icon="pot">Food, day by day</CardTitle>
          <PanelGrid series={groups.food} domain={domain} />
          <p className="mt-3 text-xs text-ink-faint">
            Each panel has its own scale, counted from zero, over the same run of days. The
            dashed line is your target. A dashed amber leg is today, still adding up.
          </p>
          <NumbersTable series={groups.food} />
        </Card>
      )}

      {groups.body.length > 0 && (
        <Card className="p-4 sm:p-5">
          <CardTitle icon="shield">Body and recovery</CardTitle>
          <PanelGrid series={groups.body} domain={domain} />
          <p className="mt-3 text-xs text-ink-faint">
            Each panel has its own scale. A target too far from the recent range is marked at
            the edge rather than flattening the line it is read against.
          </p>
          <NumbersTable series={groups.body} />
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- the roll-up

/**
 * Every target on one screen. The average is over COMPLETE days only — today's food total
 * is a running total, and averaging it in would drag the mean down by an amount that has
 * nothing to do with what was eaten. That average is what the three-week salt experiment
 * reads, so it stays strict.
 *
 * When excluding today leaves no complete day, the meter shows an em dash — never a zero —
 * but the caption names today's running total anyway. The number exists; it simply is not an
 * average yet, and six bare dashes at the top of the page is exactly what "the trends page
 * doesn't show any data" looked like the first time.
 */
function AgainstTargets({ series }: { series: WellnessTrendSeries[] }) {
  return (
    <Card className="p-4 sm:p-5">
      <CardTitle icon="pulse">Against your targets</CardTitle>
      <p className="mt-1 text-xs text-ink-faint">
        Averages over the days with something logged. Today is left out of the food averages
        while it is still adding up.
      </p>

      <ul className="mt-4 space-y-3.5">
        {series.map((s) => {
          const { avg, days } = completeAverage(s);
          // The last point, when it is today's still-accumulating total. Named in the
          // caption so a row with no complete day yet shows a NUMBER rather than only a
          // dash — without ever calling a running total an average.
          const todaySoFar =
            s.partial_last_day && s.points.length ? s.points[s.points.length - 1].value : null;
          const side = targetSide(avg, s.target, s.target_direction);
          const glyph = s.target_direction === 'up' ? '≥' : s.target_direction === 'down' ? '≤' : '~';
          const gap = avg !== null && s.target !== null ? Math.abs(avg - s.target) : null;

          return (
            <li key={s.key}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-ink">{s.label}</span>
                {/* The unit goes with the number. "— kcal" reads like a measurement that
                    failed; a bare em dash reads like the absence it is. */}
                <span className="shrink-0 text-sm text-ink-soft">
                  {avg === null ? (
                    <span className="text-ink-faint">—</span>
                  ) : (
                    <>
                      <b
                        className="text-base font-semibold text-ink"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {fmt(avg)}
                      </b>{' '}
                      {s.unit}
                    </>
                  )}
                </span>
              </div>

              <div className="mt-1.5">
                <TargetMeter
                  value={avg}
                  target={s.target}
                  direction={s.target_direction}
                  shortfall
                />
              </div>

              <p className="mt-1 text-xs">
                <span className="text-ink-faint">
                  {avg === null
                    ? // The RUNNING TOTAL is named, not just the absence of an average.
                      // Six bare em dashes at the top of the page is what "the trends page
                      // doesn't show any data" looked like the first time — and the number
                      // does exist, it just isn't an average yet. Saying it here without
                      // calling it one keeps the honesty and loses the false blank.
                      todaySoFar !== null
                      ? `${fmt(todaySoFar)} ${s.unit} today so far — not averaged until the day is done`
                      : 'nothing logged yet'
                    : `${days} ${days === 1 ? 'day' : 'days'}`}
                  {s.target !== null && ` · target ${glyph} ${fmt(s.target)} ${s.unit}`}
                </span>
                {gap !== null && side !== 'none' && (
                  <span
                    className={
                      side === 'past' ? 'ml-1.5 font-medium text-clay' : 'ml-1.5 text-ink-soft'
                    }
                  >
                    ·{' '}
                    {side === 'past'
                      ? `${s.target_direction === 'down' ? 'over' : 'under'} by ${fmt(gap)} ${s.unit}`
                      : 'within target'}
                  </span>
                )}
              </p>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------- panels

function PanelGrid({
  series,
  domain,
}: {
  series: WellnessTrendSeries[];
  domain: [number, number];
}) {
  return (
    <div className="mt-4 grid gap-x-6 gap-y-5 sm:grid-cols-2">
      {series.map((s) => (
        <Panel key={s.key} series={s} domain={domain} />
      ))}
    </div>
  );
}

function Panel({ series, domain }: { series: WellnessTrendSeries; domain: [number, number] }) {
  const last = series.points[series.points.length - 1];
  const { avg, days } = completeAverage(series);
  const glyph =
    series.target_direction === 'up' ? '≥' : series.target_direction === 'down' ? '≤' : '~';

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-medium tracking-wide text-ink-soft uppercase">{series.label}</h3>
        {series.target !== null && (
          <span className="shrink-0 text-[11px] text-ink-faint">
            {glyph} {fmt(series.target)} {series.unit}
          </span>
        )}
      </div>

      <p className="mt-0.5 flex items-baseline gap-1.5">
        <span
          className="text-xl font-semibold text-ink"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {last ? fmt(last.value) : '—'}
        </span>
        <span className="text-xs text-ink-soft">{series.unit}</span>
        <span className="text-[11px] text-ink-faint">
          {series.partial_last_day ? (
            <span className="text-honey">today, still adding up</span>
          ) : last ? (
            shortDay(last.date)
          ) : null}
        </span>
      </p>

      {/* One colour for every panel. Recolouring a whole line because its LAST point sits on
          the far side of a target would repaint history for a state that belongs to one day —
          and would spend the colour channel on something the tinted band already says. */}
      <div className="mt-1">
        <SeriesChart
          series={[series]}
          colors={[DUSK]}
          domain={domain}
          height={80}
          compact
          zeroBased={series.source === 'nutrition'}
        />
      </div>

      <p className="mt-1 text-[11px] text-ink-faint">
        {avg === null ? (
          'no complete day yet'
        ) : (
          <>
            avg <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(avg)}</span> {series.unit}{' '}
            over {days} {days === 1 ? 'day' : 'days'}
          </>
        )}
      </p>
    </div>
  );
}

/** The headline reading for a pair that is only read together, e.g. 135/81. */
function LatestBig({ series, pairAs }: { series: WellnessTrendSeries[]; pairAs: string }) {
  const dates = series.map((s) => s.points[s.points.length - 1]?.date).filter(Boolean);
  const day = dates.length ? dates.sort()[dates.length - 1] : null;
  const values = series.map((s) => {
    const p = [...s.points].reverse().find((q) => q.date === day);
    return p ? fmt(p.value) : '—';
  });

  return (
    <p className="mt-2 flex items-baseline gap-2">
      <span className="text-2xl font-semibold text-ink">{values.join(pairAs)}</span>
      <span className="text-xs text-ink-soft">{series[0]?.unit}</span>
      {day && <span className="text-[11px] text-ink-faint">{shortDay(day)}</span>}
    </p>
  );
}
